import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, CircularProgress,
  Alert, Select, MenuItem, FormControl, InputLabel, Chip,
  LinearProgress, Tooltip, TextField, Slider
} from '@mui/material';
import PeopleIcon      from '@mui/icons-material/People';
import AccessTimeIcon  from '@mui/icons-material/AccessTime';
import TrendingUpIcon  from '@mui/icons-material/TrendingUp';
import RouterIcon      from '@mui/icons-material/Router';
import { sitesAPI, dashboardAPI } from '../services/api';

function StatCard({ title, value, subtitle, icon, color, chip }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>{title}</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ my: 0.5, color }}>
              {value}
            </Typography>
            {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
            {chip && <Chip label={chip} size="small" sx={{ mt: 1, bgcolor: color + '20', color }} />}
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: color + '15' }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const DWELL_BUCKETS = [
  { key: 'lte2',  label: '<=2 min',  color: '#90CAF9', desc: 'Passage rapide' },
  { key: '2to5',  label: '2-5 min',  color: '#42A5F5', desc: 'Visite courte'  },
  { key: '5to15', label: '5-15 min', color: '#1E88E5', desc: 'Visite normale' },
  { key: 'gte15', label: '>=15 min', color: '#1565C0', desc: 'Longue visite'  },
];

function DwellChart({ dist }) {
  if (!dist) return null;
  const total  = Object.values(dist).reduce((a, b) => a + b, 0);
  const maxVal = Math.max(...DWELL_BUCKETS.map(b => dist[b.key] || 0));
  if (total === 0) return (
    <Typography color="text.secondary" variant="body2">Aucune donnee pour aujourd'hui</Typography>
  );
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 100, mb: 1 }}>
        {DWELL_BUCKETS.map(b => {
          const count    = dist[b.key] || 0;
          const pct      = maxVal > 0 ? (count / maxVal) * 100 : 0;
          const pctTotal = total  > 0 ? Math.round(count / total * 100) : 0;
          return (
            <Tooltip key={b.key} title={b.desc + ' : ' + count + ' visiteur' + (count !== 1 ? 's' : '') + ' (' + pctTotal + '%)'}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">{count}</Typography>
                <Box sx={{
                  width: '100%',
                  height: Math.max(pct, 4) + '%',
                  bgcolor: b.color,
                  borderRadius: '4px 4px 0 0',
                  minHeight: 4,
                  transition: 'height 0.4s',
                }} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {DWELL_BUCKETS.map(b => (
          <Box key={b.key} sx={{ flex: 1, textAlign: 'center' }}>
            <Box sx={{ width: 12, height: 12, bgcolor: b.color, borderRadius: 1, mx: 'auto', mb: 0.3 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              {b.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', mt: 1.5, borderRadius: 1, overflow: 'hidden', height: 8 }}>
        {DWELL_BUCKETS.map(b => {
          const pct = total > 0 ? (dist[b.key] || 0) / total * 100 : 0;
          return pct > 0
            ? <Box key={b.key} sx={{ width: pct + '%', bgcolor: b.color, transition: 'width 0.4s' }} />
            : null;
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {total} fingerprint{total !== 1 ? 's' : ''} uniques aujourd'hui (dwell &gt; 0)
      </Typography>
    </Box>
  );
}

function HourlyLineChart({ data }) {
  if (!data || data.length === 0) return (
    <Typography color="text.secondary" variant="body2">Aucune donnee pour aujourd'hui</Typography>
  );

  const W = 420, H = 120, PL = 32, PR = 8, PT = 12, PB = 24;
  const gW = W - PL - PR;
  const gH = H - PT - PB;

  // Remplir les heures manquantes avec 0
  const maxH   = data[data.length - 1].hour;
  const minH   = data[0].hour;
  const filled = [];
  for (let h = minH; h <= maxH; h++) {
    const found = data.find(d => d.hour === h);
    filled.push({ hour: h, visits: found ? found.visits : 0 });
  }

  const maxVal = Math.max(...filled.map(d => d.visits), 1);
  const xStep  = filled.length > 1 ? gW / (filled.length - 1) : gW;

  const toX = i  => PL + i * xStep;
  const toY = v  => PT + gH - (v / maxVal) * gH;

  const points  = filled.map((d, i) => [toX(i), toY(d.visits)]);
  const linePath = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const areaPath = linePath + ' L' + toX(points.length - 1).toFixed(1) + ',' + (PT + gH) + ' L' + PL.toFixed(1) + ',' + (PT + gH) + ' Z';

  // Graduation Y (0, milieu, max)
  const yTicks = [0, Math.round(maxVal / 2), maxVal];

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#1F4E79" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1F4E79" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Grille horizontale */}
        {yTicks.map(v => (
          <g key={v}>
            <line x1={PL} y1={toY(v)} x2={W - PR} y2={toY(v)}
              stroke="#E0E0E0" strokeWidth="1" strokeDasharray={v === 0 ? '0' : '3,3'} />
            <text x={PL - 4} y={toY(v) + 4} textAnchor="end"
              fontSize="9" fill="#9E9E9E">{v}</text>
          </g>
        ))}

        {/* Aire */}
        <path d={areaPath} fill="url(#areaGrad)" />

        {/* Ligne */}
        <path d={linePath} fill="none" stroke="#1F4E79" strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />

        {/* Points + labels valeur */}
        {points.map((p, i) => (
          <g key={i}>
            {filled[i].visits > 0 && (
              <text x={p[0]} y={p[1] - 5} textAnchor="middle"
                fontSize="9" fill="#1F4E79" fontWeight="600">
                {filled[i].visits}
              </text>
            )}
            <circle cx={p[0]} cy={p[1]} r="3.5" fill="#1F4E79" />
          </g>
        ))}

        {/* Axe X heures */}
        {filled.map((d, i) => (
          <text key={i} x={toX(i)} y={H - 4} textAnchor="middle"
            fontSize="9" fill="#9E9E9E">
            {d.hour}h
          </text>
        ))}
      </svg>
    </Box>
  );
}

export default function Dashboard() {
  const [sites,      setSites]      = useState([]);
  const [siteId,     setSiteId]     = useState('');
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [maxDistance,   setMaxDistance]   = useState(null); // null = pas de filtre
  const [sliderValue,   setSliderValue]   = useState(0);    // valeur affichee pendant le drag

  useEffect(() => {
    sitesAPI.list().then(r => {
      const s = r.data.sites || [];
      setSites(s);
      if (s.length > 0) setSiteId(s[0].site_id);
    }).catch(() => setError('Impossible de charger les sites'));
  }, []);

  const load = (id, date, dist) => {
    setLoading(true);
    dashboardAPI.realtime(id, date, dist)
      .then(r => { setData(r.data); setError(''); setLastUpdate(new Date().toLocaleTimeString('fr-FR')); })
      .catch(() => setError('Erreur chargement donnees'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteId) return;
    load(siteId, selectedDate, maxDistance);
    const isToday = selectedDate === new Date().toISOString().slice(0, 10);
    if (!isToday) return;
    const interval = setInterval(() => load(siteId, selectedDate, maxDistance), 300000);
    return () => clearInterval(interval);
  }, [siteId, selectedDate, maxDistance]);

  const rt = data?.realtime;
  const rssiLabel = data?.rssi_seuil ? ` (RSSI ≥ ${data.rssi_seuil} dBm)` : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1F4E79">Tableau de bord</Typography>
          <Typography variant="body2" color="text.secondary">
            {lastUpdate ? 'Mis a jour : ' + lastUpdate : 'Chargement...'}
            {' - actualise toutes les 5 min'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            type="date" size="small" label="Date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            sx={{ width: 160 }}
          />
          <Box sx={{ width: 200, px: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Distance capteur : {sliderValue > 0 ? sliderValue + ' m' : 'Toutes'}
            </Typography>
            <Slider
              value={sliderValue}
              min={0} max={20} step={1}
              marks={[{value:0,label:'0'},{value:5,label:'5m'},{value:10,label:'10m'},{value:20,label:'20m'}]}
              onChange={(_, v) => setSliderValue(v)}
              onChangeCommitted={(_, v) => setMaxDistance(v === 0 ? null : v)}
              size="small"
              sx={{ color: '#1F4E79' }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Boutique</InputLabel>
            <Select value={siteId} label="Boutique" onChange={e => setSiteId(e.target.value)}>
              {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name || s.site_id}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Visiteurs uniques (1h)"
              value={rt?.active_last_hour ?? 0}
              subtitle="Fingerprints avec dwell >= 2 min"
              icon={<PeopleIcon />} color="#1F4E79"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={(selectedDate === new Date().toISOString().slice(0, 10) ? "Passages aujourd'hui" : 'Passages ' + selectedDate) + (maxDistance ? ' (≤ ' + maxDistance + 'm)' : '')}
              value={rt?.total_today ?? 0}
              subtitle="Fingerprints avec dwell >= 2 min"
              icon={<TrendingUpIcon />} color="#2E75B6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Dwell time moyen"
              value={(rt?.avg_dwell_min ?? 0) + ' min'}
              subtitle={(rt?.avg_dwell_sec ?? 0) + ' secondes'}
              icon={<AccessTimeIcon />} color="#4CAF50"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Taux de retour"
              value={(rt?.return_rate_pct ?? 0) + '%'}
              subtitle="Visiteurs recurrents"
              icon={<TrendingUpIcon />} color="#FF9800"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography fontWeight={600} mb={2}>Distribution dwell time</Typography>
                <DwellChart dist={data.dwell_distribution} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography fontWeight={600} mb={2}>Visiteurs uniques par heure</Typography>
                <HourlyLineChart data={data.hourly_traffic} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <RouterIcon color="primary" />
                  <Typography fontWeight={600}>Capteurs</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#4CAF50">{data.sensors?.online ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">En ligne</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#F44336">{data.sensors?.offline ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Hors ligne</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#1F4E79">{data.sensors?.total ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Total</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {data.seen_count_min_client && (
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', bgcolor: '#F3F8FF' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" color="#1F4E79">
                    Seuil client : <strong>seen_count ≥ {data.seen_count_min_client}</strong> probes (~{Math.round(data.seen_count_min_client * 13 / 60)} min de présence minimum)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {data.correction?.enabled && (
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', bgcolor: '#E3F2FD' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" color="#1F4E79">
                    Coefficient correcteur WiFi actif - {data.correction.wifi_coverage_pct}% des visiteurs ont le WiFi active. Facteur applique : x{data.correction.factor}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {!data && !loading && siteId && (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
